"use client";

import React from "react";
import { FaAngleLeft } from "react-icons/fa6";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

interface FAQSectionProps {
  isSearching: boolean;
  handleBack: () => void;
  filteredQuestions: FAQItem[];
  faqSections: { title: string; items: FAQItem[] }[];
  highlightQuery: (text: string) => React.ReactNode;
}

const FAQSection: React.FC<FAQSectionProps> = ({
  isSearching,
  handleBack,
  filteredQuestions,
  faqSections,
  highlightQuery,
}) => {
  return (
    <section className="bg-[#27335d]">
      <div className="rounded-b-semifull bg-[#E9E9EA] pb-8 sm:pb-10 md:pb-14 lg:pb-16">
        <div className="rounded-b-semifull relative bg-[#4887f5] py-10 text-white md:py-16">
          {/* Results or Questions */}
          <div className="overflow-hidden px-4">
            <div key={isSearching ? "searchResults" : "faqList"}>
              {isSearching ? (
                <div>
                  <button
                    onClick={handleBack}
                    data-testid="faqBackButton"
                    className="cursor-pointer mb-10 rounded-full border-b-4 border-[#16294a] bg-[#27335D] font-bold text-white transition duration-150 ease-in-out hover:bg-[#525c7d] active:scale-95 xsm:px-3 xsm:py-1.5 xsm:text-sm sm:px-3 sm:py-1.5 sm:text-base md:ml-12 md:px-5 md:py-2 md:text-lg lg:ml-14 lg:px-6 lg:py-2.5 lg:text-xl xl:ml-16 xl:px-6 xl:py-3 xl:text-2xl"
                  >
                    <div className="flex items-center justify-center gap-1">
                      <FaAngleLeft />
                      Back
                    </div>
                  </button>
                  {filteredQuestions.length > 0 ? (
                    <div className="mx-auto mb-8 w-full max-w-lg rounded-3xl bg-white shadow-md">
                      <h3 className="rounded-t-3xl bg-[#27335D] py-5 ps-10 text-sm font-semibold text-white xsm:text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl">
                        Results
                      </h3>
                      <Accordion type="single" collapsible>
                        {filteredQuestions.map((item: FAQItem) => (
                          <AccordionItem key={item.id} value={item.id} className="border-b-0">
                            <hr className="mx-auto w-[90%]" />
                            <AccordionTrigger className="px-4 py-2 hover:no-underline [&>svg]:text-[#667085]">
                              <h4 className="text-left text-xs font-semibold text-[#27335D] xsm:text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl">
                                {highlightQuery(item.question)}
                              </h4>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4 text-[0.50rem] font-semibold text-[#7C85A1] xsm:text-[0.50rem] sm:text-xs md:text-sm lg:text-base xl:text-lg">
                              {highlightQuery(item.answer)}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>
                  ) : (
                    <p className="text-center text-lg font-bold text-white">
                      No results found.
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div className="mx-auto w-[80%] text-start">
                    <h1 className="pb-3 text-xl font-bold tracking-tight text-[#27335D] xsm:text-xl sm:text-2xl md:text-5xl lg:text-6xl xl:text-7xl">
                      Common Questions, Quick{" "}
                      <span className="md:block">Answers!</span>
                    </h1>
                  </div>
                  <div className="mt-6 grid grid-cols-1 gap-x-3 gap-y-10 px-4 sm:grid-cols-1 md:mt-10 md:grid-cols-2 md:gap-y-36 lg:grid-cols-2">
                    {faqSections.map((section, index) => (
                      <div
                        key={index}
                        className="mx-auto h-fit w-full max-w-lg"
                      >
                        <h3 className="rounded-t-3xl bg-[#27335D] py-5 ps-10 text-sm font-semibold text-white xsm:text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl">
                          {section.title}
                        </h3>
                        <div className="rounded-3xl rounded-t-none border-2 border-[#27335D] bg-white">
                          <Accordion type="single" collapsible>
                            {section.items.map((item: FAQItem) => (
                              <AccordionItem key={item.id} value={item.id} className="border-b-0">
                                <hr className="mx-auto w-[90%]" />
                                <AccordionTrigger className="px-4 py-2 hover:no-underline [&>svg]:text-[#667085]">
                                  <h4 className="text-left text-xs font-semibold text-[#27335D] xsm:text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl">
                                    {highlightQuery(item.question)}
                                  </h4>
                                </AccordionTrigger>
                                <AccordionContent className="px-4 pb-4 text-[0.50rem] font-semibold text-[#7C85A1] xsm:text-[0.50rem] sm:text-xs md:text-sm lg:text-base xl:text-lg">
                                  {highlightQuery(item.answer)}
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
